import {Component, Input, OnInit} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';

import {
    addCurrency,
    BaseModalComponent,
    CashAudit,
    CashAuditActions,
    CashAuditEvents,
    CashDrawer,
    CashDrawerService,
    currency,
    Donation,
    formatCurrencyToNumeric,
    Order,
    OrderService,
    PickupLocation,
    PickupLocationService,
    PrinterService,
    subtractCurrency,
    Tab
} from 'brewbill-lib';
import {AssignCashDrawerComponent} from '../assign-cash-drawer/assign-cash-drawer.component';

@Component({
    selector: 'bb-cash-payment',
    templateUrl: './cash-payment.component.html',
    styleUrls: ['./cash-payment.component.scss'],
})
export class CashPaymentComponent extends BaseModalComponent implements OnInit {
    @Input() tab: Tab;
    @Input() silent = false;

    order: Order;
    formGroup: FormGroup = new FormGroup({});
    cashDrawer: CashDrawer;
    pickupLocation: PickupLocation;
    due: number;
    totalDue: number;
    closing = false;
    requestDonation = false;
    donation: number;
    presets: number[];

    constructor(
        private formBuilder: FormBuilder,
        private pickupLocationService: PickupLocationService,
        private cashDrawerService: CashDrawerService,
        private orderService: OrderService,
        private printerService: PrinterService,
        modalController: ModalController,
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.order = this.orderService.currentValue;
        this.cashDrawer = this.cashDrawerService.currentValue;
        this.pickupLocation = this.pickupLocationService.currentValue;

        this.requestDonation = !!this.pickupLocation && !!this.pickupLocation.activeDonationProgram;
        this.adjustIncludedTaxes();

        const amount = !!this.totalDue ? this.totalDue : this.due;
        this.setPresets(amount);

        this.formGroup = this.formBuilder.group(
            {
                tabId: this.tab.id,
                amount: [
                    Number.parseFloat(amount.toFixed(2)),
                    [currency(false, 0, amount)]
                ],
                received: ['', [Validators.required, Validators.min(0)]],
                tip: '',
                action: [CashAuditActions.IN, Validators.required],
                event: [CashAuditEvents.PAYMENT, Validators.required]
            }
        );

        this.subscribe(this.orderService.current.subscribe(o => {
            if (!this.closing) {
                this.order = !!o ? new Order(o) : null;
            }
        }));

        this.subscribe(this.cashDrawerService.current.subscribe(c => {
            this.cashDrawer = !!c ? new CashDrawer(c) : null;
            if (!this.cashDrawer && !this.closing) {
                this.close();
            } else {
                if (!this.cashDrawer.trackCashTips) {
                    this.formGroup.controls.tip.setValue(0);
                }
            }
        }));
    }

    setPresets(amount: number) {
        const presets = [];
        if (!!amount) {
            const five = Math.ceil(amount / 5) * 5;
            const ten = Math.ceil(amount / 10) * 10;
            const twenty = Math.ceil(amount / 20) * 20;
            presets.push(five);
            if (five !== ten) {
                presets.push(ten);
            }
            if (twenty !== ten) {
                presets.push(twenty);
            }
        }
        this.presets = presets;
    }

    adjustIncludedTaxes() {
        let d = this.tab.paymentInformation.remaining;
        if (!!this.tab.taxes && !this.tab.taxExemptionId) {
            this.tab.orders.forEach(o => {
                o.items.forEach(oi => {
                    if (!!oi.taxes && oi.taxes.length > 0) {
                        oi.taxes.filter(t => !t.taxIncluded).forEach(tax => {
                            if (((!!oi.menuItem && !oi.menuItem.taxOverride) || !!oi.membershipRenewal)
                                && tax.taxIncludedCash
                                && (this.tab.paymentInformation.paymentType === 'NONE'
                                    || this.tab.paymentInformation.paymentType === 'CASH'
                                    || (this.tab.paymentInformation.paymentType === 'CHARGE'
                                        && this.tab.paymentInformation.chargePaymentsTotal === 0))) {
                                d = subtractCurrency(d, tax.tax);
                            }
                        });
                    }
                });
            });
        }
        this.due = d;

        if (!!this.tab.paymentInformation.depositsRemaining) {
            this.totalDue = addCurrency(this.due, this.tab.paymentInformation.depositsRemaining);
        }
    }

    exact() {
        this.quickCash();
        this.addAction();
    }

    handleDonation(donation: number) {
        this.donation = donation;
        this.setPresets(this.totalWithDonation());
        this.requestDonation = false;
    }

    totalWithDonation() {
        return addCurrency(!!this.totalDue ? this.totalDue : this.due, this.donation);
    }

    addAction() {
        const audit = new CashAudit(this.formGroup.value);
        audit.parentId = this.cashDrawer.id;
        audit.received = formatCurrencyToNumeric(audit.received);

        if (!!this.donation) {
            audit.donation = new Donation({
                amount: this.donation,
                donationProgram: this.pickupLocation.activeDonationProgram
            });
        }

        const principal = subtractCurrency(subtractCurrency(audit.received, audit.tip), this.donation);

        if (principal >= audit.amount && !!this.tab.paymentInformation.depositsRemaining) {
            audit.deposit = this.tab.paymentInformation.depositsRemaining;
            audit.amount = subtractCurrency(audit.amount, audit.deposit);
        } else if (principal < audit.amount) {
            audit.amount = principal;
        }

        audit.amount = addCurrency(audit.amount, this.donation);

        audit.fullPayment = audit.amount === this.due;
        audit.change = this.change();

        if (this.printerService.isConnected()) {
            this.printerService.openDrawer();
        }

        this.close(audit);
    }

    change() {
        const change =
            subtractCurrency(formatCurrencyToNumeric(this.formGroup.controls.received.value),
                addCurrency(addCurrency(this.formGroup.controls.amount.value, this.formGroup.controls.tip.value), this.donation));
        return !!this.formGroup.controls.received.value && change > 0 ? change : 0;
    }

    changeAsTip() {
        this.formGroup.controls.tip.setValue(addCurrency(this.formGroup.controls.tip.value, this.change()).toFixed(2));
    }

    quickCash() {
        this.formGroup.controls.received.setValue(addCurrency(this.formGroup.controls.amount.value, this.donation));
    }

    async changeDrawer() {
        const modal = await this.modalController.create({
            component: AssignCashDrawerComponent,
            componentProps: {
                pickupLocation: this.pickupLocation
            }
        });

        await modal.present();
    }
}
